<script lang="ts" setup>
import { watch, ref, computed } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { EMIT } from '@/enum';
import { useTenantStore, useAuthStore } from '@/store';
import { storeToRefs } from 'pinia';

const tenantStore = useTenantStore();
const authStore = useAuthStore();
const { tenant } = storeToRefs(tenantStore);
const editing = ref('');

interface TenantForm {
  name: string | undefined;
}
/**
 * The general reference object to contain the form data so vuelidate works
 */
const tenantForm = ref<TenantForm>({ name: '' });

/**
 * need to watch the tenant store for val changes
 */
watch(tenantStore, () => {
  tenantForm.value.name = tenant?.value?.name;
  resetForm();
});

/**
 * The required validation rules.
 */
const rules = { name: { required, minLength: minLength(3) } };

/**
 * Define all the available emit events.
 */
const emit = defineEmits<{ (e: EMIT.SAVE, name: string): void }>();

/**
 * Reset the vuelidate dirty status for all keys.
 */
const resetDirty = () => v$.value.$reset();

/**
 * Initialize the vuelidate instance.
 */
const v$ = useVuelidate(rules, tenantForm);

/**
 * reset the stype for the outline and footer class and dirty
 */
const resetForm = () => {
  editing.value = '';
  resetDirty();
};

const onSave = () => {
  emit(EMIT.SAVE, tenantForm.value.name as string);
  resetForm();
};

const onCancel = () => {
  tenantForm.value.name = tenant?.value?.name;
  resetForm();
};

const setStyle = () => {
  editing.value = 'editing';
};

const isMasterTenant = computed(() => tenant?.value?.id === authStore.masterTenantId);
</script>

<template>
  <Card class="general" :class="editing">
    <template #header>
      <h2>General</h2>
    </template>
    <template #content>
      <form class="w-full">
        <div class="formgrid grid">
          <div class="field col">
            <label for="name">Name</label>
            <Skeleton v-if="tenantStore.loading" height="2.3rem" class="w-full" />
            <InputText
              v-else
              id="name"
              v-model="v$.name.$model"
              class="w-full"
              :disabled="isMasterTenant"
              @input="setStyle"
            />
          </div>
        </div>
      </form>
    </template>
    <template #footer>
      <template v-if="v$.$dirty">
        <AppButton id="cancel_btn" type="secondary" text="Cancel" @click="onCancel" />
        <AppButton id="save_btn" type="primary" text="Save" :disabled="v$.$invalid" @click="onSave" />
      </template>
    </template>
  </Card>
</template>

<style lang="scss" scoped>
:deep(.p-card) {
  border-radius: 1rem;
  box-shadow: none;
  overflow: hidden;
  border: 1px solid white;
}
:deep(.p-card-body) {
  height: 505px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

:deep(.p-card-content) {
  height: 100%;
}
:deep(.p-card-footer) {
  min-height: 72px;
  border-radius: 0 0 13px 13px;
  justify-content: flex-end;
  padding: 0;
  padding-right: 16px;
  align-items: center;
}
.editing {
  border-color: var(--primary-color-medium) !important;

  &:deep(.p-card-footer) {
    display: flex;
    background-color: var(--primary-color-light);
  }
}
</style>
